import type { BookingConfirmationSummary, MyBookingDetail } from '@massilia/queries'

/**
 * Maps a MyBookingDetail (from the booking API) to a BookingConfirmationSummary
 * so that existing components like TripRecapSection can be reused without modification.
 */
export function mapBookingDetailToConfirmationSummary(
  booking: MyBookingDetail
): BookingConfirmationSummary {
  return {
    bookingId: booking.id,
    agency: booking.agency,
    bookingReference: booking.reference,
    carRentals: booking.carRentals.map((cr) => ({
      company: cr.company,
      details: cr.details,
      dropOff: cr.dropOff,
      location: cr.location,
      pickUp: cr.pickUp,
      price: cr.price,
    })),
    client: {
      email: booking.client.email,
      phone: booking.client.phoneNumbers?.[0]?.number ?? null,
    },
    conditions:
      booking.exchangeAllowed != null || booking.refundAllowed != null
        ? {
            exchangeable: {
              allowed: booking.exchangeAllowed ?? false,
              feeDescription: booking.exchangeFeeDescription ?? 'Non modifiable',
            },
            refundable: {
              allowed: booking.refundAllowed ?? false,
              feeDescription: booking.refundFeeDescription ?? 'Non remboursable',
            },
          }
        : undefined,
    currency: booking.currency,
    depositAmountCents: booking.depositAmount ?? undefined,
    depositDeadline: booking.depositDeadline ?? undefined,
    extras: booking.extras.map((e) => ({
      fileName: e.fileName,
      name: e.name,
      providerName: e.providerName,
      description: e.description,
      fileUrl: e.fileUrl,
      price: e.price,
      providerExternalRef: e.providerExternalRef,
    })),
    flightSegments: booking.flightSegments.map((seg) => ({
      airline: seg.airline,
      arrivalAt: seg.arrivalAt,
      arrivalLocation: seg.arrivalLocation,
      arrivalUtcOffset: seg.arrivalUtcOffset,
      cabinLuggageCount: seg.cabinLuggageCount,
      cabinLuggageWeight: seg.cabinLuggageWeight,
      checkedLuggageCount: seg.checkedLuggageCount,
      checkedLuggageWeight: seg.checkedLuggageWeight,
      departureAt: seg.departureAt,
      departureLocation: seg.departureLocation,
      departureUtcOffset: seg.departureUtcOffset,
      description: seg.description,
      durationMinutes: seg.durationMinutes,
      travelClass: seg.travelClass,
    })),
    hotels: booking.hotels.map((h) => ({
      name: h.name,
      checkIn: h.checkIn,
      checkOut: h.checkOut,
      hotelUrl: h.hotelUrl,
      location: h.location,
      pensionType: h.pensionType,
      price: h.price,
      roomType: h.roomType,
    })),
    insurance:
      booking.insurance && booking.insuranceActivatedAt
        ? {
            cgsLink: booking.insurance.cgsLink,
            dinLink: booking.insurance.dinLink,
            nomAssurance: booking.insurance.nomAssurance,
            nomFormule: booking.insurance.nomFormule,
            price: booking.insurance.price,
          }
        : null,
    insuranceEstimate: booking.insuranceEstimate,
    passengers: booking.passengers.map((p) => ({
      firstName: p.firstName,
      lastName: p.lastName,
      birthDate: p.birthDate,
      gender: p.gender,
    })),
    status: booking.status,
    totalPriceCents: booking.totalPrice,
  }
}
